import React, { useState, useEffect } from 'react';
import _ from 'lodash';
import PageHeaderTitle from '../../../components/PageHeaderTitle';
import './style.scss';
import TableContainer from './TableContainer';
import { ActionType } from './ITable';
import { INews, INewsModalData, INewsState, ModalType } from './news';
import { sortAutoItems } from '../../../utils/sortHelper';
import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import { selectUser } from '../../../slice/userSlice';
import { dateFormatter } from '../../../utils/dateHelper';
import { dateFormat2, dateFormat3 } from '../../../constants/dateConstant';
import { CCol, CRow } from '@coreui/react';
import { CXButton, CXTextInput } from '../../../components/CXForm';
import { useLocation } from 'react-router-dom';
import Access from '../../../components/Access';
import { AccessType } from '../../../constants/roleAccessConstant';
import { createNews, deleteNews, getNewsList, updateNews } from '../../../services/newsAPI';
import { pushToast } from '../../../slice/appSlice';
import { defaultChartDisplay, itemColumns, tableColumns } from '../../../constants/newsConstant';
import AddOrEditNewsModal from './AddOrEditNewsModal';
import { DeleteNewsModal } from './DeleteNewsModal';

/**
 * Application home page component
 * @returns {JSX}
 */

const initialState: INewsState = {
  keyword: '',
  newsList: [],
  emptyNewsModal: {
    _id: '',
    validFrom: '',
    validTo: '',
    title: '',
    content: '',
  },
  isShowModal: false,
  modalType: ModalType.NULL,
};

const NewsPage = () => {
  const dispatch = useAppDispatch();
  const { pathname } = useLocation();
  const { userId } = useAppSelector(selectUser);
  const [keyword, setKeyword] = useState<string>(initialState.keyword);
  const [newsList, setNewsList] = useState<INews[]>(initialState.newsList);
  const [modalType, setModalType] = useState<ModalType>(initialState.modalType);
  const [modalData, setModalData] = useState<INewsModalData>(initialState.emptyNewsModal);

  useEffect(() => {
    onClickSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onClickSearch = () => {
    getNewsList(keyword)
      .then(([isSucceed, errorMsg, respData]) => {
        if (!isSucceed) {
          return;
        }
        setNewsList(respData as INews[]);
      })
      .catch((err) => {
        console.log(err);
      });
  };

  const onClickAddOrUpdateNews = (newsPayload: INewsModalData) => {
    // if add new news, remove _id field
    const isAddNews = modalType === ModalType.ADD;
    if (isAddNews && newsPayload['_id'] !== undefined) {
      delete newsPayload['_id'];
    }

    // add updatedBy field
    newsPayload[itemColumns.updatedBy.dbField] = userId;

    const apiCall = isAddNews ? createNews(newsPayload) : updateNews(newsPayload);
    const successMessage = `News is successfully ${isAddNews ? 'created' : 'edited'}`;

    apiCall
      .then(([isSucceed,]) => {
        if (!isSucceed) {
          return;
        }
        setModalType(ModalType.NULL);
        const now = dateFormatter(new Date(), dateFormat3);
        dispatch(pushToast({ type: "success", message: `${successMessage} at ${now}.` }));
        onClickSearch();
      })
      .catch((err) => {
        console.log(err);
      });
  };

  const onClickDeleteNews = () => {
    if (!modalData._id) {
      return;
    }
    const id = modalData._id;
    deleteNews(id).then(([isSucceed,]) => {
      if (!isSucceed) {
        return;
      }
      setModalType(ModalType.NULL);
      const now = dateFormatter(new Date(), dateFormat3);
      dispatch(pushToast({ type: "success", message: `News is successfully deleted at ${now}.` }));
      onClickSearch();
    }).catch((err) => {
      console.log(err);
    });
  };

  const sortByLocale = (key: string) => {
    const { sortedList } = sortAutoItems([...newsList], key);
    const sortedSidStarList = sortedList.map(item => ({ ...item }));
    setNewsList(sortedSidStarList);
  }

  const actionsCallback = (actionType: ActionType, data: any) => {
    switch (actionType) {
      case ActionType.ADD_OPEN:
        setModalData(initialState.emptyNewsModal);
        setModalType(ModalType.ADD);
        break;
      case ActionType.ADD:
      case ActionType.EDIT_SAVE:
        onClickAddOrUpdateNews(data);
        break;
      case ActionType.EDIT_OPEN:
      case ActionType.DELETE_OPEN:
        // get id from formatted data [[{dbField: '_id', value: '123'}, ...], ...]       
        const itemID = data && data.find((item) => item.dbField === '_id')?.value;
        const newsItem = newsList.find((news) => news._id === itemID) || initialState.emptyNewsModal;
        setModalData(newsItem as INewsModalData);
        setModalType(actionType === ActionType.EDIT_OPEN ? ModalType.EDIT : ModalType.DELETE);
        break;
      case ActionType.DELETE_CONFIRM:
        onClickDeleteNews();
        break;
      case ActionType.CLOSE_MODAL:
        setModalType(ModalType.NULL);
        break;
      case ActionType.SORT:
        sortByLocale(data);
        break;
      default:
        break;
    }
  };

  const renderSearchBar = () => {
    return (
      <>
        {
          <>
            <div className={"d-flex justify-content-between searchBar"}>
              <CRow>
                <CCol sm="auto">
                  <div className={"searchBox"} style={{ width: 462 }}>
                    <CXTextInput
                      placeholder={"Search by title/ content"}
                      value={keyword}
                      onChange={setKeyword}
                      zIndex={1029}
                    />
                  </div>
                </CCol>
                <CCol sm="auto">
                  <div className={"searchButton"}>
                    <div>
                      <CXButton
                        text={"Search"}
                        onClick={() => {
                          onClickSearch();
                        }}
                        fontSize={"16px"}
                        width={"100%"}
                      />
                    </div>
                  </div>
                </CCol>
              </CRow>

              <Access route={pathname} access={[AccessType.CREATE]}>
                <CRow className={"p-2 ms-auto"}>
                  <CXButton
                    onClick={() => {
                      actionsCallback(ActionType.ADD_OPEN, {});
                    }}
                    text={"Add News"}
                    outline={true}
                    fontSize={"14px"}
                  />
                </CRow>
              </Access>
            </div>
          </>
        }
      </>
    );
  }

  const dateFieldFormat = (date: string, format: string = dateFormat2) => {
    return _.isEmpty(date) ? defaultChartDisplay : dateFormatter(date, format);
  }

  // format news list for table display, add dbField, value, isHidden, needBreakSpace, like below format [[{dbField: 'id', value: '123'}, ...], ...]
  const getFormatNewsList = (newsListIn: INews[]) => {
    return newsListIn && newsListIn.length > 0 ? newsListIn.map((newsItem: INews) => [
      { dbField: itemColumns.id.dbField, value: newsItem._id, isHidden: true },
      { dbField: itemColumns.validFrom.dbField, value: dateFieldFormat(newsItem.validFrom) },
      { dbField: itemColumns.validTo.dbField, value: dateFieldFormat(newsItem.validTo) },
      { dbField: itemColumns.title.dbField, value: newsItem.title || defaultChartDisplay, needBreakSpace: true },
      { dbField: itemColumns.content.dbField, value: newsItem.content || defaultChartDisplay, needBreakSpace: true },
      { dbField: itemColumns.updatedAt.dbField, value: `${newsItem?.updatedBy || defaultChartDisplay}\n${dateFieldFormat(newsItem?.updatedAt || '', dateFormat3)}`, needBreakSpace: true },
      { dbField: itemColumns.actions.dbField, value: [ActionType.EDIT, ActionType.DELETE], isHidden: true },
    ]) : [];
  };

  const TableContainerProps = {
    tbColumns: tableColumns,
    dataList: getFormatNewsList(newsList),
    actionsCallback,
    tableType: 'news',
  };

  const AddOrEditNewsModalProps = {
    modalType,
    modalData,
    actionsCallback,
  };

  const DeleteNewsModalProps = {
    isShow: modalType === ModalType.DELETE,
    actionsCallback,
    dataList: [
      { name: 'Valid From', value: dateFieldFormat(modalData.validFrom) },
      { name: 'Valid To', value: dateFieldFormat(modalData.validTo) },
      { name: 'Title', value: modalData.title || defaultChartDisplay },
      { name: 'Content', value: modalData.content || defaultChartDisplay }
    ],
  };

  return (
    <div className='newsPage'>
      {/* page title */}
      <PageHeaderTitle headerTitle="News" />
      {/* search bar content */}
      {renderSearchBar()}
      {/* table list */}
      <TableContainer {...TableContainerProps} />
      {/* Add or Edit News Modal */}
      {!!modalType
        && ([ModalType.ADD, ModalType.EDIT].includes(modalType))
        && <AddOrEditNewsModal {...AddOrEditNewsModalProps} />
      }
      {/* Delete News Modal */}
      {!!modalType
        && modalType === ModalType.DELETE
        && <DeleteNewsModal {...DeleteNewsModalProps} />
      }
    </div>
  );
};

export default NewsPage;
